import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HelpCircle, Plus, Trash2, Save, ChevronUp, ChevronDown, GripVertical, Upload, Download, X as XIcon, AlertTriangle } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { question_en: "", question_ar: "", answer_en: "", answer_ar: "", category: "", audience: "platform", active: true };

function FaqEditor({ open, initial, audience, categories, onClose, onSaved }) {
  const [f, setF] = useState(initial || { ...EMPTY, audience });
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState("");
  useEffect(() => { if (open) { setF(initial || { ...EMPTY, audience }); setNewCat(""); } }, [open, initial, audience]);
  async function save() {
    const cat = newCat.trim() || f.category || "";
    const payload = { ...f, category: cat, audience: f.audience || audience };
    if (!payload.question_en.trim() || !payload.question_ar.trim() || !payload.answer_en.trim() || !payload.answer_ar.trim()) {
      toast.error("EN + AR question and answer are all required.");
      return;
    }
    setSaving(true);
    try {
      if (f.id) await api.put(`/faq/${f.id}`, payload);
      else await api.post("/faq", payload);
      toast.success(f.id ? "FAQ updated" : "FAQ added");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="faq-editor-dialog">
        <DialogHeader>
          <DialogTitle>
            {f.id ? "Edit FAQ" : "Add FAQ"}
            <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-[#0B7E8C] bg-[#E6F5F7] px-1.5 py-[1px] rounded">
              {(f.audience || audience) === "mobile" ? "Mobile app" : "Platform"}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">
            Both English and Arabic question + answer are required. Pick an existing category or type a new one.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Question (English) *</div>
              <Textarea rows={2} value={f.question_en} onChange={(e) => setF({ ...f, question_en: e.target.value })}
                        maxLength={300} placeholder="e.g. How do I reset my password?"
                        data-testid="faq-question-en" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Question (Arabic) *</div>
              <Textarea rows={2} dir="rtl" value={f.question_ar} onChange={(e) => setF({ ...f, question_ar: e.target.value })}
                        maxLength={300} placeholder="مثال: كيف يمكنني إعادة تعيين كلمة المرور؟"
                        data-testid="faq-question-ar" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Answer (English) *</div>
              <Textarea rows={6} value={f.answer_en} onChange={(e) => setF({ ...f, answer_en: e.target.value })}
                        maxLength={5000} data-testid="faq-answer-en" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Answer (Arabic) *</div>
              <Textarea rows={6} dir="rtl" value={f.answer_ar} onChange={(e) => setF({ ...f, answer_ar: e.target.value })}
                        maxLength={5000} data-testid="faq-answer-ar" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Category</div>
              <div className="flex gap-2">
                <Select value={f.category || "__none__"} onValueChange={(v) => setF({ ...f, category: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="flex-1" data-testid="faq-category-select">
                    <SelectValue>{f.category || "— pick an existing —"}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— none —</SelectItem>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="Or new category…" value={newCat} onChange={(e) => setNewCat(e.target.value)}
                       maxLength={80} className="max-w-[220px]" data-testid="faq-new-category" />
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Active</div>
              <div className="flex items-center gap-2 h-9">
                <Switch checked={!!f.active} onCheckedChange={(v) => setF({ ...f, active: v })}
                        data-testid="faq-active-switch" />
                <span className="text-sm text-[#374151]">{f.active ? "Visible" : "Hidden"}</span>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
                  data-testid="faq-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, audience, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  useEffect(() => { if (open) { setFile(null); setResult(null); } }, [open]);

  function onPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) { toast.error("Please pick a .csv file."); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("CSV must be under 2 MB."); return; }
    setFile(f); setResult(null);
  }

  async function downloadTemplate() {
    try {
      const r = await api.get("/faq/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `faq_${audience}_template.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function submit() {
    if (!file) { toast.error("Pick a CSV first."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/faq/import?audience=${audience}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      if (r.data.inserted > 0) toast.success(`Imported ${r.data.inserted} ${audience} FAQ${r.data.inserted === 1 ? "" : "s"}`);
      if (r.data.inserted === 0 && r.data.skipped > 0) toast.error("No FAQs imported. See errors below.");
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="faq-import-dialog">
        <DialogHeader>
          <DialogTitle>
            Import FAQs from CSV
            <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-[#0B7E8C] bg-[#E6F5F7] px-1.5 py-[1px] rounded">
              {audience === "mobile" ? "Mobile app" : "Platform"}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">
            Required columns: <b>question_en, question_ar, answer_en, answer_ar</b>. Optional: <b>category</b>, <b>active</b>. Rows will be added to the <b>{audience}</b> section unless a row's own <b>audience</b> column overrides it. Up to 500 rows per file.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <button type="button" onClick={downloadTemplate}
            className="text-xs text-[#0B7E8C] hover:text-[#128C97] underline inline-flex items-center gap-1"
            data-testid="faq-import-template-btn">
            <Download className="w-3.5 h-3.5" /> Download CSV template
          </button>

          <div>
            <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onPick}
                   data-testid="faq-import-file-input" />
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}
                className="border-[#0B7E8C]/40 text-[#0B7E8C] hover:bg-[#0B7E8C]/5"
                data-testid="faq-import-pick-btn">
                <Upload className="w-4 h-4 mr-1.5" /> {file ? "Change file" : "Choose CSV"}
              </Button>
              {file && (
                <span className="inline-flex items-center gap-1 text-xs text-[#374151]"
                      data-testid="faq-import-file-chip">
                  {file.name} <span className="text-[10px] text-[#9CA3AF]">({Math.round(file.size / 1024)} KB)</span>
                  <button type="button" onClick={() => setFile(null)} className="text-[#9CA3AF] hover:text-red-600">
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}
            </div>
          </div>

          {result && (
            <div className="rounded-md border border-[#E5E7EB] bg-slate-50 p-3 text-sm" data-testid="faq-import-result">
              <div>
                <span className="font-semibold text-emerald-700">{result.inserted} inserted</span>
                {" · "}
                <span className={`font-semibold ${result.skipped ? "text-amber-700" : "text-[#6B7280]"}`}>{result.skipped} skipped</span>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-2 text-[12px] text-amber-800">
                  <div className="font-semibold flex items-center gap-1 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Skipped rows
                  </div>
                  <ul className="space-y-0.5 max-h-40 overflow-y-auto pr-1" data-testid="faq-import-errors">
                    {result.errors.map((e, i) => (
                      <li key={i} className="text-[11px]">
                        <b>Row {e.row}:</b> {e.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button onClick={submit} disabled={uploading || !file}
              className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="faq-import-submit-btn">
              <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Importing…" : "Import"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FaqAdmin() {
  const [items, setItems] = useState(null);
  const [categories, setCategories] = useState([]);
  const [counts, setCounts] = useState({ platform: 0, mobile: 0 });
  const [audience, setAudience] = useState(() => localStorage.getItem("faq_admin_audience") || "platform");
  const [editorOpen, setEditorOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filterCat, setFilterCat] = useState("");
  useEffect(() => { try { localStorage.setItem("faq_admin_audience", audience); } catch {} }, [audience]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/faq/admin?audience=${audience}`);
      setItems(r.data.items || []);
      setCategories(r.data.categories || []);
      // Fetch full counts across both audiences to render the tab badges
      const both = await api.get("/faq/admin");
      setCounts(both.data.counts || { platform: 0, mobile: 0 });
    } catch (e) { toast.error(formatApiError(e)); setItems([]); }
  }, [audience]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setFilterCat(""); }, [audience]);

  const filtered = useMemo(() => (items || []).filter((i) => !filterCat || i.category === filterCat), [items, filterCat]);

  async function move(i, dir) {
    const j = i + dir;
    if (!items || j < 0 || j >= filtered.length) return;
    // Reorder within full list index; find the actual full-list positions
    const list = items.slice();
    const orig = list.findIndex((x) => x.id === filtered[i].id);
    const other = list.findIndex((x) => x.id === filtered[j].id);
    if (orig < 0 || other < 0) return;
    [list[orig], list[other]] = [list[other], list[orig]];
    setItems(list);
    setBusy(true);
    try {
      await api.post("/faq/reorder", { ordered_ids: list.map((f) => f.id) });
    } catch (e) { toast.error(formatApiError(e)); load(); }
    finally { setBusy(false); }
  }

  async function remove(f) {
    if (!window.confirm(`Delete "${f.question_en}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/faq/${f.id}`);
      toast.success("FAQ deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function toggle(f) {
    setBusy(true);
    try {
      await api.put(`/faq/${f.id}`, { ...f, active: !f.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="faq-admin-page">
      <PageHeader
        title="FAQ Management"
        subtitle="Add, edit, activate, and reorder frequently asked questions shown on the platform Help section and mobile app."
        icon={HelpCircle}
      />
      <div className="max-w-5xl">
        <div className="inline-flex items-center rounded-md border border-[#E5E7EB] bg-white p-0.5 mb-4" data-testid="faq-audience-tabs">
          {[
            { k: "platform", label: "Platform FAQs" },
            { k: "mobile", label: "Mobile App FAQs" },
          ].map(({ k, label }) => (
            <button
              key={k}
              type="button"
              onClick={() => setAudience(k)}
              className={`px-3 py-1.5 text-xs rounded ${audience === k ? "bg-[#0B7E8C] text-white" : "text-[#6B7280]"}`}
              data-testid={`faq-audience-tab-${k}`}
            >
              {label} <span className={`ml-1 text-[10px] ${audience === k ? "text-white/80" : "text-[#9CA3AF]"}`}>({counts[k] || 0})</span>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <Select value={filterCat || "all"} onValueChange={(v) => setFilterCat(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full md:w-[220px]" data-testid="faq-admin-filter">
              <SelectValue>{filterCat || "All categories"}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => { setEditing(null); setEditorOpen(true); }}
                  className="bg-[#0B7E8C] hover:bg-[#128C97] text-white h-8" data-testid="faq-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add FAQ
          </Button>
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}
                  className="border-[#0B7E8C]/40 text-[#0B7E8C] hover:bg-[#0B7E8C]/5 h-8" data-testid="faq-import-btn">
            <Upload className="w-4 h-4 mr-1" /> Import CSV
          </Button>
        </div>

        {items === null ? (
          <div className="text-sm text-[#6B7280]" data-testid="faq-admin-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="hah-card p-8 text-center text-sm text-[#6B7280]" data-testid="faq-admin-empty">
            No FAQs yet. Click "Add FAQ" to publish the first one.
          </div>
        ) : (
          <div className="hah-card divide-y divide-[#E5E7EB]">
            {filtered.map((f, i) => (
              <div key={f.id} className="p-4 flex items-start gap-3" data-testid={`faq-row-${f.id}`}>
                <div className="flex flex-col gap-1 pt-1">
                  <button className="text-[#6B7280] hover:text-[#0B7E8C] disabled:opacity-30"
                          onClick={() => move(i, -1)} disabled={i === 0 || busy}
                          data-testid={`faq-up-${f.id}`}><ChevronUp className="w-4 h-4" /></button>
                  <GripVertical className="w-4 h-4 text-[#D1D5DB]" />
                  <button className="text-[#6B7280] hover:text-[#0B7E8C] disabled:opacity-30"
                          onClick={() => move(i, 1)} disabled={i === filtered.length - 1 || busy}
                          data-testid={`faq-down-${f.id}`}><ChevronDown className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {f.category && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-[#0B7E8C] bg-[#E6F5F7] px-1.5 py-[1px] rounded">{f.category}</span>
                    )}
                    {!f.active && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] bg-slate-100 px-1.5 py-[1px] rounded">Inactive</span>
                    )}
                  </div>
                  <div className="mt-1 text-sm font-medium text-[#1F2937]">{f.question_en}</div>
                  <div className="text-sm text-[#6B7280]" dir="rtl">{f.question_ar}</div>
                  <div className="mt-1 text-[12px] text-[#374151] line-clamp-2 whitespace-pre-wrap">{f.answer_en}</div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <Switch checked={!!f.active} onCheckedChange={() => toggle(f)} disabled={busy}
                          data-testid={`faq-active-toggle-${f.id}`} />
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(f); setEditorOpen(true); }}
                            className="h-7 text-xs" data-testid={`faq-edit-${f.id}`}>Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => remove(f)}
                            className="border-[#E5E7EB] text-red-600 hover:bg-red-50 h-7 text-xs"
                            data-testid={`faq-delete-${f.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <FaqEditor open={editorOpen} initial={editing} audience={audience} categories={categories}
                 onClose={() => setEditorOpen(false)} onSaved={load} />
      <ImportDialog open={importOpen} audience={audience} onClose={() => setImportOpen(false)} onDone={load} />
    </div>
  );
}
